import { useMemo, useState } from 'react';
import { formatPercentPoints, type StrainSummary } from '@shared';

/**
 * Searchable strain selector showing name, THC %, producer, and a thumbnail.
 * Large tap targets for phone use. Selection returns the full summary so the
 * caller can snapshot potency at calculation time.
 */
export interface StrainSelectProps {
  label: string;
  strains: StrainSummary[];
  selectedId: string | null;
  onSelect: (strain: StrainSummary) => void;
  /** Strain IDs that must not appear as selectable options. */
  excludeIds?: string[];
}

export function StrainSelect({
  label,
  strains,
  selectedId,
  onSelect,
  excludeIds = [],
}: StrainSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = strains.find((s) => s.id === selectedId) ?? null;
  const exclude = useMemo(() => new Set(excludeIds), [excludeIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return strains.filter((s) => {
      if (exclude.has(s.id) && s.id !== selectedId) return false;
      if (!q) return true;
      const hay = `${s.name ?? ''} ${s.productName ?? ''} ${s.producer ?? ''}`;
      return hay.toLowerCase().includes(q);
    });
  }, [search, strains, exclude, selectedId]);

  return (
    <div className="strain-select field" style={{ margin: 0 }}>
      <label>{label}</label>

      {selected && !open ? (
        <div className="selected-strain">
          <ThumbFor strain={selected} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>
              {selected.name ?? selected.productName}
            </div>
            <div className="muted" style={{ fontSize: '0.82rem' }}>
              {formatPercentPoints(selected.thcPercent)} THC
              {selected.producer ? ` · ${selected.producer}` : ''}
            </div>
          </div>
          <button type="button" className="btn" onClick={() => setOpen(true)}>
            Change
          </button>
        </div>
      ) : (
        <>
          <input
            type="search"
            placeholder="Search strains…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={`Search for ${label}`}
            autoFocus={open}
          />
          {strains.length === 0 ? (
            <p className="hint">
              No strains yet. Add some in the catalogue first.
            </p>
          ) : (
            <div className="option-list" role="listbox">
              {filtered.map((s) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={s.id === selectedId}
                  key={s.id}
                  className="option-row"
                  onClick={() => {
                    onSelect(s);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <ThumbFor strain={s} />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>
                      {s.name ?? s.productName}
                    </span>
                    <br />
                    <span className="muted" style={{ fontSize: '0.8rem' }}>
                      {formatPercentPoints(s.thcPercent)} THC
                      {s.producer ? ` · ${s.producer}` : ''}
                    </span>
                  </span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="option-row muted">No matches</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ThumbFor({ strain }: { strain: StrainSummary }): JSX.Element {
  if (strain.primaryThumbnailUrl) {
    return <img src={strain.primaryThumbnailUrl} alt="" className="strain-thumb" />;
  }
  return (
    <span className="strain-thumb placeholder" aria-hidden="true">
      🌿
    </span>
  );
}
